/**
 * Light / dark / system mode for Brand Center's own chrome (SASBND-0008 U2).
 *
 * Owns the operator's mode *preference* (`light | dark | system`, default
 * `system`), resolves `system` from `prefers-color-scheme`, builds the MUI theme
 * from the matching `ChromeTokens` set, and publishes both through context so the
 * top-bar toggle can read and change it.
 *
 * The preference is persisted to `localStorage`. That is a UI setting, not a
 * credential — the repo's never-persist guardrail is about auth tokens, which
 * stay in memory in `authStore` and are untouched here. Storage is treated as
 * optional throughout (Safari private mode and locked-down profiles throw on
 * access), so a failure degrades to "this tab only" rather than breaking boot.
 *
 * This provider themes the *app chrome*. The tenant login preview renders the
 * tenant's own brand and must not follow it — see SASBND-0008 U3.
 */
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { createAppTheme } from './theme';
import { DARK_CHROME, LIGHT_CHROME } from './tokens';

/** The stored preference. `system` tracks the OS/browser setting live. */
export type ThemeMode = 'light' | 'dark' | 'system';

/** What actually renders once `system` has been resolved. */
export type ResolvedThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'saas_brand.themeMode';

const MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];

function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (MODES as readonly string[]).includes(value);
}

/** Last stored preference, or `system` when absent, unreadable or corrupt. */
function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function writeStoredMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Storage unavailable — the mode still applies for the life of this tab.
  }
}

interface ThemeModeContextValue {
  /** The operator's preference, including `system`. */
  mode: ThemeMode;
  /** The mode being rendered — `system` already resolved against the OS. */
  resolvedMode: ResolvedThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export interface ThemeModeProviderProps {
  children: ReactNode;
  /** Test seam: start from an explicit preference instead of stored/`system`. */
  initialMode?: ThemeMode;
}

export function ThemeModeProvider({ children, initialMode }: ThemeModeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => initialMode ?? readStoredMode());
  // Subscribed, so a `system` preference follows the OS switching mid-session.
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const resolvedMode: ResolvedThemeMode =
    mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    writeStoredMode(next);
  }, []);

  const theme = useMemo(
    () => createAppTheme(resolvedMode === 'dark' ? DARK_CHROME : LIGHT_CHROME),
    [resolvedMode],
  );

  const value = useMemo<ThemeModeContextValue>(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within a ThemeModeProvider');
  return ctx;
}
